import React, { useEffect, useState } from 'react';
import Moment from "moment/moment";
import { getComparator, stableSort } from '../SortTableHelper';
import { IPurchasedItem } from 'app/services/reporting/reporting.type';
import { MoreVert, KeyboardArrowDown, KeyboardArrowRight } from '@material-ui/icons';
import { IPurchasedItemsTableHeader, IPurchasedItemsTableProps, Order } from '../types';
import { ProductCategories, AppendDollarToAmount, ConvertAmountToDollar } from '../helper';
import { Box, Collapse, createStyles, IconButton, makeStyles, Menu, MenuItem, Table, TableBody, TableCell, TableHead, TableRow, TableSortLabel, Typography, withStyles } from '@material-ui/core';

const NS = 'PurchasedItemsTable';

/** Styles for purchased items table */
const useStyles = makeStyles({
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
  itemMenu: {
    marginTop: 40
  },
  date: {
    marginLeft: 4
  }
});

/** Display the table header background with light blue color */
const StyledTableCell = withStyles(() =>
  createStyles({
    head: {
      backgroundColor: '#08bffb26'
    }
  })
)(TableCell);

/** Table header */
const headerCells: IPurchasedItemsTableHeader[] = [
  { id: 'transactionDateTime', label: 'Date' },
  { id: 'sku', label: 'SKU' },
  { id: 'productName', label: 'Item' },
  { id: 'description', label: 'Description' },
  { id: 'unitPrice', label: 'Price' },
  { id: 'quantity', label: 'Qty' },
  { id: 'salesCategoryName', label: 'Category' }
];

/**
 * Displays the list of purchased items in tabular format
 */
export const PurchasedItemsTable = (props: IPurchasedItemsTableProps) => {

  const classes = useStyles();
  const [order, setOrder] = useState<Order>('asc');
  const [selectedCategoryIndex, setCategory] = useState(0);
  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);
  const [orderBy, setOrderBy] = useState<keyof IPurchasedItem>('transactionDateTime');
  const [purchasedItemGroups, setPurchasedItemGroups] = useState<Record<string, IPurchasedItem[]>>(null);

  useEffect(() => {
    const productGroupList = getProductGroupList(props.purchasedItemList);
    setPurchasedItemGroups(productGroupList);

    // Collapse all the accordions
    return () => {
      setCategory(0);
      setOrderBy('transactionDateTime');
    }
  }, [props.purchasedItemList]);

  /** Filter the table rows as per the selected category i.e. food, beverage, etc. */
  const filterItemsByCategory = (selectedIndex: number) => {

    let itemList: IPurchasedItem[] = props.purchasedItemList;

    // Do not filter when "Show all" category is selected
    if (selectedIndex > 0) {
      itemList = props.purchasedItemList.filter(item => item.salesCategoryName.toLowerCase() === ProductCategories[selectedIndex].toLowerCase());
    }

    const list = getProductGroupList(itemList);

    // Update the table rows
    setPurchasedItemGroups(list);

    setCategory(selectedIndex);

    // Close the category menu list
    setAnchorElement(null);

    // Expand all the accordions
    setOrderBy('salesCategoryName');
  }

  /** Make product group list */
  const getProductGroupList = (purchasedItemList: IPurchasedItem[]) => {
    const productGroupList = purchasedItemList.reduce((list: Record<string, IPurchasedItem[]>, item: IPurchasedItem) => {
      list[item.transactionDateTime] = list[item.transactionDateTime] || [];
      list[item.transactionDateTime].push(item);
      return list;
    }, {});
    return productGroupList;
  }

  /** Sort the column in asc/desc order */
  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof IPurchasedItem) => {
    /**
     * isAsc is false, when a new column is selected for sorting in ascending order.
     * isAsc is true, when the same column is selected again for sorting but, this time in descending order.
     */
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  /**
   * Renders the item list, grouped as per the transaction date
   */
  const PurchasedItemList = () => {

    // Items purchased recently, appear at the top of the list
    const transactionDateList = Object.keys(purchasedItemGroups).sort((x,y) => Moment(x).isAfter(Moment(y)) ? -1 : 1);

    // Iterate through each date
    const list = transactionDateList.map((key: string, dateIndex: number) => {

      // Expand/Collapse the item list
      const [isExpanded, setAccordionState] = React.useState(orderBy !== 'transactionDateTime');

      // Iterate through each item purchased on the provided date
      const itemList = stableSort(purchasedItemGroups[key], getComparator(order, orderBy)).map((item: IPurchasedItem, index: number) => {

        const price = ConvertAmountToDollar(item.unitPrice);

        return (
          <TableRow key={index}>
            <TableCell style={{ paddingLeft: 14 }}>{Moment(item.transactionDateTime).format('DD MMM YYYY hh:mm A')}</TableCell>
            <TableCell>{item.sku}</TableCell>
            <TableCell>{item.productName}</TableCell>
            <TableCell>{item.description}</TableCell>
            <TableCell>{AppendDollarToAmount(price)}</TableCell>
            <TableCell>{item.quantity}</TableCell>
            <TableCell>{item.salesCategoryName}</TableCell>
          </TableRow>
        );
      });

      return (
        <React.Fragment key={dateIndex}>
          <TableRow hover onClick={() => setAccordionState(!isExpanded)}>
            <TableCell colSpan={7} style={{backgroundColor: isExpanded && 'rgba(0, 0, 0, 0.04)'}}>
              <IconButton aria-label="expand row" size="small">
                {isExpanded ? <KeyboardArrowDown /> : <KeyboardArrowRight/>}
              </IconButton>
              <span className={classes.date}>{Moment(key).format('DD MMM YYYY hh:mm A')}</span>
            </TableCell>
          </TableRow>
          {isExpanded ? itemList : null}
        </React.Fragment>
      )
    });

    return <>{list}</>;
  }

  return (
    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={13}>
      <Collapse in={props.isOpen} timeout="auto" unmountOnExit>
        <Box margin={1}>
          <Typography variant="h6" gutterBottom component="div">Purchased Items</Typography>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
              {
                headerCells.map(header => (
                  header.label === 'Date' ?
                  <StyledTableCell key={header.id} style={{ paddingLeft: 14, minWidth: 90 }}>{header.label}</StyledTableCell> :
                  <StyledTableCell key={header.id} sortDirection={orderBy === header.id ? order : false}>
                      <TableSortLabel
                      active={orderBy === header.id}
                      direction={orderBy === header.id ? order : 'asc'}
                      onClick={(e) => handleRequestSort(e, header.id)}>
                      {header.label}
                      {
                        orderBy === header.id ? (
                          <span className={classes.visuallyHidden}>
                            {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                          </span>
                        ) : null
                      }
                      </TableSortLabel>
                    {
                      props.purchasedItemList.length > 0 && header.id === "salesCategoryName" &&
                      <>
                        <IconButton onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorElement(event.currentTarget)}>
                          <MoreVert />
                        </IconButton>
                        <Menu className={classes.itemMenu} open={!!anchorElement} anchorEl={anchorElement}
                          onClose={() => setAnchorElement(null)}>
                          {
                            ProductCategories.map((product, index) => {
                              return <MenuItem key={index} selected={index === selectedCategoryIndex} onClick={() => filterItemsByCategory(index)}>{product}</MenuItem>
                            })
                          }
                        </Menu>
                      </>
                    }
                  </StyledTableCell>
                ))
              }
              </TableRow>
            </TableHead>
            <TableBody>{!!purchasedItemGroups ? <PurchasedItemList /> : null}</TableBody>
          </Table>
        </Box>
      </Collapse>
    </TableCell>
  )
}
